import { motion } from 'motion/react';
import { useState } from 'react';
import { useGalleryImages, heroFallbackImage } from '@/pages/home/galleryImages';

export default function StoreGallerySection() {
  const { images: loadedImages, loading } = useGalleryImages();
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const openLightbox = (idx: number) => setSelectedIndex(idx);
  const closeLightbox = () => setSelectedIndex(null);
  const goPrev = () => {
    if (selectedIndex === null) return;
    setSelectedIndex((selectedIndex - 1 + loadedImages.length) % loadedImages.length);
  };
  const goNext = () => {
    if (selectedIndex === null) return;
    setSelectedIndex((selectedIndex + 1) % loadedImages.length);
  };

  const selected = selectedIndex !== null ? loadedImages[selectedIndex] : null;

  return (
    <section id="gallery" className="py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="text-center mb-12 md:mb-16"
        >
          <span className="inline-block bg-accent-100 text-accent-900 px-4 py-1.5 rounded-full text-xs md:text-sm font-semibold mb-4">
            Visit Us
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-4">
            Our Store Gallery
          </h2>
          <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
            Take a look at our store on Tumkur-Koratagere Highway. Come visit us for mobiles, appliances, DTH, and more.
          </p>
        </motion.div>

        {loading && loadedImages.length === 0 && (
          <div className="text-center py-16">
            <div className="inline-flex items-center gap-3 text-foreground-500">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin text-lg"></i>
              </span>
              <span className="text-sm">Scanning gallery images...</span>
            </div>
          </div>
        )}

        {!loading && loadedImages.length === 0 && (
          <div className="text-center py-16">
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              No images found yet. Upload your photos to{' '}
              <code className="bg-background-200 px-1.5 py-0.5 rounded text-xs font-mono">
                https://ashoktelecom.com/images/
              </code>
              {' '}and upload{' '}
              <code className="bg-background-200 px-1.5 py-0.5 rounded text-xs font-mono">
                gallery-list.php
              </code>
              {' '}there too.
            </p>
            <p className="text-foreground-500 text-xs mt-3">
              Supports .jpg, .jpeg, .png, .webp — any filename, max 20 images
            </p>
          </div>
        )}

        {loadedImages.length > 0 && (
          <>
            {loading && (
              <p className="text-center text-foreground-500 text-xs mb-6">
                <span className="w-4 h-4 inline-flex items-center justify-center mr-1">
                  <i className="ri-loader-4-line animate-spin"></i>
                </span>
                Loading images... {loadedImages.length} found so far
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {loadedImages.map((photo, index) => {
                const isTall = index === 2 || index === 5;
                return (
                  <motion.div
                    key={photo.src}
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-60px' }}
                    transition={{ duration: 0.5, delay: (index % 6) * 0.1, ease: 'easeOut' }}
                    onClick={() => openLightbox(index)}
                    className={`group relative rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer ${isTall ? 'sm:row-span-2' : ''}`}
                  >
                    <div className={`w-full ${isTall ? 'h-64 sm:h-full' : 'h-56 md:h-64'}`}>
                      <img
                        src={photo.src}
                        alt={photo.alt}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4 md:p-5">
                      <div>
                        <p className="text-background-50 font-semibold text-sm md:text-base">{photo.caption}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-zoom-in-line text-background-200 text-xs"></i>
                          </span>
                          <span className="text-background-300 text-xs">Click to enlarge</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-background-50 hover:text-background-200 transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>

          {loadedImages.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goPrev(); }}
                className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-background-50/20 hover:bg-background-50/30 rounded-full text-background-50 transition-colors cursor-pointer z-10"
              >
                <i className="ri-arrow-left-line text-lg"></i>
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goNext(); }}
                className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-background-50/20 hover:bg-background-50/30 rounded-full text-background-50 transition-colors cursor-pointer z-10"
              >
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </>
          )}

          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={selected.src}
              alt={selected.alt}
              className="w-full max-h-[75vh] object-contain rounded-lg"
            />
            <p className="text-background-200 text-center mt-3 text-sm md:text-base font-medium">
              {selected.caption}
            </p>
          </div>
        </div>
      )}
    </section>
  );
}